


































import EntryMixin from "@/mixins/http/Entry";
import { AdcontrolProfile, Brand, Company, Product, User } from "@/models";
import ClientUserInfo from "@/models/Agents/UserInfo";
import { ProfileCapabilitiesService } from "@/services/Profile/CapabilitiesService";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import EntryListGroup from "./EntryListGroup.vue";

const DEFAULT_PLACEHOLDER = "Cerca aziende/brand/prodotti";
const DEFAULT_MIN = 3;
const DEFAULT_DELAY = 750;

const REQUEST_TAG = "entries";

export interface EntrySearchResult {
  companies: Company[];
  brands: Brand[];
  products: Product[];
  total: number;
}

@Component({ components: { EntryListGroup } })
export default class EntrySearch extends Mixins(EntryMixin) {
  @Prop({ default: DEFAULT_PLACEHOLDER }) readonly placeholder?: string;
  @Prop({ default: DEFAULT_MIN }) readonly min?: number;
  @Prop({ default: DEFAULT_DELAY }) readonly delay?: number;
  @Prop({ default: false }) readonly selectable?: boolean;
  @Prop({ default: "right" }) readonly iconAlign?: string;
  @Prop() readonly size?: string;
  @Prop() readonly label?: string | undefined;

  @Prop({ default: true }) readonly companiesEnabled?: boolean;
  @Prop({ default: true }) readonly brandsEnabled?: boolean;
  @Prop({ default: true }) readonly productsEnabled?: boolean;

  term = this.label ?? "";
  timeout: number | undefined;

  companies: Company[] = [];
  brands: Brand[] = [];
  products: Product[] = [];

  searching = false;

  dropdownVisible = false;

  @Getter("user") readonly user!: User;
  @Getter("agentUserInfos") readonly agentUserInfo?: ClientUserInfo;
  @Getter("profile") readonly adcontrolProfile!: AdcontrolProfile;

  @Watch("label", { immediate: true })
  onLabelUpdated() {
    this.term = this.label ?? "";
  }

  get results(): EntrySearchResult {
    return {
      companies: this.companies,
      brands: this.brands,
      products: this.products,
      total: this.companies.length + this.brands.length + this.products.length,
    };
  }

  get queryTerm(): string {
    return `%${this.term}%`;
  }

  get showTitles(): boolean {
    return (
      Number(this.companiesEnabled) +
        Number(this.brandsEnabled) +
        Number(this.productsEnabled) >
      1
    );
  }

  get termStrippedBySpaces(): string {
    return this.term.replace(/\s/g, "");
  }

  async search() {
    if (this.termStrippedBySpaces.length === 0) {
      return;
    }

    this.searching = true;
    await this.searchAll();
    this.searching = false;
    this.$emit("searched", this.results);

    if (this.selectable) {
      this.showDropdown();
    }
  }
  get queryParams(): any {
    let baseParams: any = {
      "filter[name]": this.queryTerm,
      "page[size]": "all",
    };

    if (
      ProfileCapabilitiesService.agentsModuleEnabled(
        this.user.profile,
        this.agentUserInfo
      ) &&
      !this.agentUserInfo?.superUser
    ) {
      baseParams = {
        ...baseParams,
        agent_module_view: true,
        profile: this.adcontrolProfile.slug,
      };
    }

    return baseParams;
  }

  async searchCompanies() {
    this.companies = await this.getCompanies(this.queryParams, REQUEST_TAG);
  }

  async searchBrands() {
    this.brands = await this.getBrands(this.queryParams, REQUEST_TAG);
  }

  async searchProducts() {
    this.products = await this.getProducts(this.queryParams, REQUEST_TAG);
  }

  public focus() {
    (this.$refs.input as HTMLInputElement).focus();
  }

  searchAll() {
    const promises = [];

    if (this.companiesEnabled) {
      promises.push(this.searchCompanies());
    }

    if (this.brandsEnabled) {
      promises.push(this.searchBrands());
    }

    if (this.productsEnabled) {
      promises.push(this.searchProducts());
    }

    return Promise.all(promises);
  }

  clearSearch() {
    this.companies = [];
    this.brands = [];
    this.products = [];
    this.term = "";
  }

  emitResults() {
    this.$emit("searched", this.results);
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  protected showDropdown() {
    this.dropdownVisible = true;
  }

  protected closeDropdown() {
    this.dropdownVisible = false;
  }

  protected onKeyUp() {
    if (this.min && this.termStrippedBySpaces.length >= this.min) {
      this.clearTimeout();

      this.timeout = setTimeout(() => {
        this.search();
      }, this.delay);
    } else if (this.termStrippedBySpaces.length == 0) {
      this.clearSearch();
      this.emitResults();
    }
  }

  protected onSelected(value: Company | Brand | Product) {
    this.term = value.name;
    this.$emit("selected", value.clone());
    this.closeDropdown();
  }
}
